import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCart } from '@/lib/cart';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { formatCOP } from '@/lib/format';
import { Trash2, ShoppingBag, ShieldCheck } from 'lucide-react';
import { ProductImage } from '@/components/ProductImage';
import { toast } from 'sonner';
import { CART, CHECKOUT } from '@/constants/testIds';
import { CityCombobox } from '@/components/CityCombobox';
import { useSEO } from '@/lib/useSEO';
import { resolveImageUrl } from '@/components/ImageUploader';
import { paymentReturnOrigin, openWompiCheckout } from '@/lib/native';
import { ShippingQuote } from '@/components/ShippingQuote';

export default function Cart() {
  const { items, updateQty, remove, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [form, setForm] = useState({ address: '', department: '', city: '', phone: '' });
  const [departments, setDepartments] = useState([]);
  const [ship, setShip] = useState({ total: 0, blocked: false, ready: false });
  const [quoteNonce, setQuoteNonce] = useState(0);
  const grandTotal = subtotal + (ship.total || 0);
  const [submitting, setSubmitting] = useState(false);
  const [payConfig, setPayConfig] = useState({ mode: 'simulated', wompi: { enabled: false } });
  const [acceptLegal, setAcceptLegal] = useState(false);
  // Versiones vigentes de los documentos legales aceptados en el checkout.
  // Se cargan desde /api/legal/{slug} para que la orden registre la versión
  // real que el comprador está aceptando en este momento (no un valor
  // hardcodeado). Cuando el admin publique nuevas versiones, este flujo
  // capta automáticamente la versión current sin necesidad de deploy.
  const [legalVersions, setLegalVersions] = useState({ terms: null, purchase: null });

  useSEO({
    title: 'Carrito de compras | AutoMarket Colombia',
    description: 'Revisa los repuestos en tu carrito, ajusta cantidades y realiza el pago seguro.',
    canonical: '/carrito',
  });

  useEffect(() => {
    let ok = true;
    api.get('/config/payments').then(({ data }) => { if (ok) setPayConfig(data || {}); }).catch(() => {});
    api.get('/geo/departments').then(({ data }) => { if (ok) setDepartments(data.items || []); }).catch(() => {});
    fetchLegalVersions();
    return () => { ok = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch versiones cada vez que el usuario ABRE la sección de checkout,
  // así detectamos si el admin publicó nuevas versiones mientras el buyer
  // tenía el carrito abierto.
  useEffect(() => {
    if (checkoutOpen) fetchLegalVersions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutOpen]);

  async function fetchLegalVersions() {
    try {
      const [t, p] = await Promise.all([
        api.get('/legal/terms').catch(() => ({ data: null })),
        api.get('/legal/purchase').catch(() => ({ data: null })),
      ]);
      const nextT = t.data?.version || null;
      const nextP = p.data?.version || null;
      setLegalVersions((prev) => {
        // Si detectamos versión diferente a la que el usuario ya había
        // aceptado, resetear el checkbox y avisar.
        if (
          acceptLegal && (
            (prev.terms && nextT && prev.terms !== nextT) ||
            (prev.purchase && nextP && prev.purchase !== nextP)
          )
        ) {
          setAcceptLegal(false);
          toast.info('Los términos legales fueron actualizados. Revísalos y acepta nuevamente para continuar.');
        }
        return { terms: nextT, purchase: nextP };
      });
    } catch {
      /* noop */
    }
  }

  const wompiEnabled = !!payConfig?.wompi?.enabled;

  const onPay = async () => {
    if (!user) { nav('/login', { state: { from: '/carrito' } }); return; }
    if (user.role !== 'buyer') { toast.error('Solo cuentas de comprador pueden comprar'); return; }
    if (!form.address || !form.city || !form.phone) { toast.error('Completa la dirección de envío'); return; }
    if (!acceptLegal) { toast.error('Debes aceptar los Términos y Condiciones y la Política de Compras.'); return; }
    if (!ship.ready || ship.blocked) { toast.error('Revisa el envío: hay vendedores sin cobertura o falta el departamento.'); return; }

    setSubmitting(true);
    try {
      const paymentMethod = wompiEnabled ? 'wompi' : 'simulated';
      const { data } = await api.post('/orders/checkout', {
        items: items.map((x) => ({ product_id: x.product_id, qty: x.qty })),
        shipping_address: form.address,
        shipping_city: form.city,
        shipping_department: form.department || undefined,
        shipping_quote_total: ship.total,
        shipping_phone: form.phone,
        payment_method: paymentMethod,
        accept_terms: true,
        legal_terms_version: legalVersions.terms || 'unknown',
        legal_policies_version: legalVersions.purchase || 'unknown',
      });

      if (paymentMethod === 'wompi') {
        // Iniciar pago con Wompi Web Checkout (redirect hospedado por Wompi).
        const { data: init } = await api.post('/payments/wompi/init', {
          checkout_id: data.checkout_id,
          customer_email: user.email,
        });
        const redirectUrl = `${paymentReturnOrigin()}${init.redirect_url_path}?ref=${encodeURIComponent(init.reference)}`;
        const params = new URLSearchParams({
          'public-key': init.public_key,
          'currency': init.currency,
          'amount-in-cents': String(init.amount_in_cents),
          'reference': init.reference,
          'signature:integrity': init.integrity_signature,
          'redirect-url': redirectUrl,
          'customer-data:email': init.customer_email || user.email || '',
          'customer-data:full-name': user.name || '',
          'customer-data:phone-number': form.phone || '',
        });
        // NO vaciamos el carrito aquí: si Wompi rechaza el pago, el buyer
        // debe poder volver a /carrito, cambiar el método de pago y
        // reintentar. El `clear()` se ejecuta en PaymentResult.jsx solo
        // cuando el webhook confirma el estado 'paid' (APPROVED).
        // Redirigir al Checkout Web de Wompi (sandbox o prod según ENV en backend).
        await openWompiCheckout(`${init.checkout_url}?${params.toString()}`, init.reference);
        return;
      }

      // Simulated path (legacy MVP)
      clear();
      const count = (data.orders || [data.order]).length;
      if (count > 1) {
        toast.success(`Pago simulado exitoso · Se generaron ${count} pedidos (uno por vendedor)`);
      } else {
        toast.success(`Pago simulado exitoso · Pedido ${data.order.order_id}`);
      }
      nav('/mis-pedidos');
    } catch (e) {
      // Reaceptación forzada cuando el backend detecta versión desactualizada
      // (nuevas T&C publicadas mientras el buyer tenía el carrito abierto).
      if (e.response?.status === 409 && e.response?.data?.detail?.code === 'legal_version_mismatch') {
        const cur = e.response.data.detail.current || {};
        setLegalVersions({ terms: cur.terms_version, purchase: cur.policies_version });
        setAcceptLegal(false);
        toast.error(e.response.data.detail.message || 'Los términos fueron actualizados. Acepta la nueva versión.');
      } else {
        const d = e.response?.data?.detail;
        if (d?.code === 'shipping_changed') setQuoteNonce((n) => n + 1);
        toast.error(typeof d === 'string' ? d : (d?.message || 'Error al procesar el pago'));
      }
    } finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Carrito</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Tu pedido</h1>

        {items.length === 0 ? (
          <div className="mt-10 border border-slate-200 bg-slate-50 p-12 text-center">
            <ShoppingBag className="mx-auto h-10 w-10 text-slate-400" />
            <p className="mt-4 text-slate-600">Tu carrito está vacío</p>
            <Link to="/catalogo" className="mt-4 inline-block">
              <Button className="rounded-none bg-slate-900 hover:bg-slate-800">Explorar catálogo</Button>
            </Link>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr,360px] gap-6">
            <div className="border border-slate-200 bg-white divide-y divide-slate-100">
              {items.map((it) => (
                <div data-testid={CART.itemRow(it.product_id)} key={it.product_id} className="flex gap-4 p-4">
                  <div className="h-24 w-24 shrink-0 overflow-hidden">
                    <ProductImage
                      src={resolveImageUrl(it.image)}
                      alt={it.name || ''}
                      className="h-full w-full object-cover"
                      iconSize="h-8 w-8"
                      testid={`cart-item-image-${it.product_id}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-slate-900 line-clamp-2">{it.name}</div>
                    <div className="text-sm mt-0.5 flex items-center gap-2 flex-wrap">
                      {it.has_promotion ? (
                        <>
                          <span
                            data-testid={`cart-item-price-original-${it.product_id}`}
                            className="line-through text-slate-400"
                          >
                            {formatCOP(it.list_price)} c/u
                          </span>
                          <span
                            data-testid={`cart-item-price-effective-${it.product_id}`}
                            className="text-orange-600 font-bold"
                          >
                            {formatCOP(it.price)} c/u
                          </span>
                          <span
                            data-testid={`cart-item-discount-${it.product_id}`}
                            className="inline-flex items-center bg-orange-100 text-orange-700 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                          >
                            −{it.discount_pct}%
                          </span>
                        </>
                      ) : (
                        <span className="text-slate-500">{formatCOP(it.price)} c/u</span>
                      )}
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="flex items-center border border-slate-300">
                        <button onClick={() => updateQty(it.product_id, it.qty - 1)} className="h-8 w-8 hover:bg-slate-100">−</button>
                        <input
                          data-testid={CART.qtyInput(it.product_id)}
                          value={it.qty}
                          onChange={(e) => updateQty(it.product_id, parseInt(e.target.value) || 1)}
                          className="h-8 w-10 text-center border-x border-slate-300 outline-none text-sm"
                        />
                        <button onClick={() => updateQty(it.product_id, it.qty + 1)} className="h-8 w-8 hover:bg-slate-100">+</button>
                      </div>
                      <button
                        data-testid={CART.removeBtn(it.product_id)}
                        onClick={() => remove(it.product_id)}
                        className="text-xs text-rose-700 hover:text-rose-900 inline-flex items-center gap-1"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Eliminar
                      </button>
                    </div>
                  </div>
                  <div className="font-display font-bold text-right shrink-0">
                    {it.has_promotion && (
                      <div
                        data-testid={`cart-item-line-original-${it.product_id}`}
                        className="text-xs line-through text-slate-400 font-normal"
                      >
                        {formatCOP(it.list_price * it.qty)}
                      </div>
                    )}
                    <div
                      data-testid={`cart-item-line-total-${it.product_id}`}
                      className={it.has_promotion ? 'text-orange-600' : 'text-slate-900'}
                    >
                      {formatCOP(it.price * it.qty)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <aside className="border border-slate-200 bg-white p-5 h-fit">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Resumen</div>
              <div className="mt-4 flex items-center justify-between">
                <span className="text-slate-600">Subtotal</span>
                <span data-testid={CART.subtotal} className="font-display font-bold text-slate-900">{formatCOP(subtotal)}</span>
              </div>
              {items.some((x) => x.has_promotion) && (
                <div className="mt-1 flex items-center justify-between text-xs">
                  <span className="text-orange-700">Ahorros por promociones</span>
                  <span data-testid="cart-savings-total" className="text-orange-700 font-bold">
                    −{formatCOP(items.reduce((s, x) => s + Math.max(0, (x.list_price || x.price) - x.price) * x.qty, 0))}
                  </span>
                </div>
              )}
              <div className="mt-2 border-t border-slate-100 pt-2">
                <ShippingQuote items={items} department={form.department} city={form.city} onChange={setShip} nonce={quoteNonce} />
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between">
                <span className="font-semibold text-slate-900">Total a pagar{ship.legacy ? ' (sin envíos a convenir)' : ''}</span>
                <span data-testid="cart-grand-total" className="font-display text-lg font-black text-slate-900">{formatCOP(grandTotal)}</span>
              </div>

              {!checkoutOpen ? (
                <Button
                  data-testid={CART.checkoutBtn}
                  onClick={() => setCheckoutOpen(true)}
                  className="mt-5 w-full rounded-none bg-orange-500 hover:bg-orange-400 text-slate-900 h-11 font-bold"
                >
                  Continuar al pago
                </Button>
              ) : (
                <div className="mt-5 space-y-3">
                  <Input
                    data-testid={CHECKOUT.addressInput}
                    placeholder="Dirección de envío"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="rounded-none"
                  />
                  <select
                    data-testid="checkout-department-select"
                    value={form.department}
                    onChange={(e) => setForm({ ...form, department: e.target.value })}
                    className="w-full h-10 border border-slate-300 px-2 text-sm bg-white"
                  >
                    <option value="">Departamento</option>
                    {departments.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  <CityCombobox
                    testId={CHECKOUT.cityInput}
                    value={form.city}
                    onChange={(city) => setForm({ ...form, city })}
                    placeholder="Ciudad"
                  />
                  <Input
                    data-testid={CHECKOUT.phoneInput}
                    placeholder="Teléfono de contacto"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="rounded-none"
                  />
                  <div className="text-xs text-slate-600 border border-dashed border-slate-300 p-2 bg-slate-50 flex items-start gap-2" data-testid="cart-payment-mode-banner">
                    <ShieldCheck className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
                    <div>
                      {wompiEnabled ? (
                        <>Pago seguro con <strong>Wompi</strong> ({payConfig?.wompi?.env === 'production' ? 'producción' : 'sandbox'}). Serás redirigido a la pasarela para completar tu pago.</>
                      ) : (
                        <>Pago <strong>simulado</strong> (modo MVP). Integración Wompi lista para activarse.</>
                      )}
                    </div>
                  </div>
                  <div
                    data-testid={CHECKOUT.legalDisclaimer}
                    className="text-xs leading-relaxed border border-slate-200 bg-white p-3 text-slate-600"
                  >
                    <p>
                      <strong className="text-slate-900">AutoMarket Colombia</strong> facilita la conexión entre compradores y vendedores. El envío lo realiza el vendedor o AutoMarket, según se indica en cada pedido, con la tarifa y el tiempo mostrados arriba. Las garantías aplicables al producto corresponden al vendedor y/o fabricante, según la naturaleza de cada operación y la legislación vigente.
                    </p>
                    <label className="mt-3 flex items-start gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        data-testid={CHECKOUT.legalAcceptCheckbox}
                        checked={acceptLegal}
                        onChange={(e) => setAcceptLegal(e.target.checked)}
                        className="mt-0.5 h-4 w-4 accent-slate-900 shrink-0"
                      />
                      <span className="text-slate-700">
                        He leído y acepto los{' '}
                        <Link
                          to="/terminos"
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={CHECKOUT.legalTermsLink}
                          className="text-blue-700 hover:underline font-semibold"
                        >
                          Términos y Condiciones
                        </Link>{' '}
                        y la{' '}
                        <Link
                          to="/politicas"
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={CHECKOUT.legalPoliciesLink}
                          className="text-blue-700 hover:underline font-semibold"
                        >
                          Política de Compras
                        </Link>
                        .
                      </span>
                    </label>
                  </div>
                  <Button
                    data-testid={CHECKOUT.payBtn}
                    onClick={onPay}
                    disabled={submitting || !acceptLegal || !ship.ready || ship.blocked}
                    className="w-full rounded-none bg-slate-900 hover:bg-slate-800 h-11 font-bold disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Procesando...' : (wompiEnabled ? `Pagar ${formatCOP(grandTotal)} con Wompi` : `Pagar ${formatCOP(grandTotal)}`)}
                  </Button>
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </Layout>
  );
}
